import { useEffect, useRef, useState, useCallback } from 'react';
import { Snackbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useParticipantEPR } from './ParticipantEPRContext';

export default function EPRSocketBridge({ socket, liveId }) {
  const { reload } = useParticipantEPR();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const debounceRef = useRef(null);

  // --- sound refs/state ---
  const audioRef = useRef(null);
  const [soundReady, setSoundReady] = useState(false);

  // Prepare the ding audio and unlock after first user gesture
  useEffect(() => {
    const a = new Audio('/sounds/success.mp3'); // place file in public/sounds/ding.mp3
    a.preload = 'auto';
    a.volume = 0.4; // tweak to taste
    audioRef.current = a;

    // Unlock audio on first user interaction (autoplay policy)
    const unlock = () => {
      a.play()
        .then(() => {
          a.pause();
          a.currentTime = 0;
          setSoundReady(true);
        })
        .catch(() => {
          // still locked; user will trigger again later
        });
      window.removeEventListener('pointerdown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });

    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  const playDing = useCallback(() => {
    if (!soundReady || !audioRef.current) return;
    // Clone so overlapping sounds don't cut each other off
    const a = audioRef.current.cloneNode();
    a.volume = audioRef.current.volume;
    a.play().catch(() => {});
  }, [soundReady]);

  // Socket routes
  useEffect(() => {
    if (!socket || !liveId) return;

    const onBloodsUpdated = (payload) => {
      if (payload?.livescenarioid && payload.livescenarioid !== liveId) return;

      // Sound + toast
      playDing();
      setToastMsg('New blood results received');
      setToastOpen(true);

      // Debounced reload (stale-while-revalidate)
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        reload();
      }, 150);
    };

    const onObsUpdated = (payload) => {
      if (payload?.livescenarioid && payload.livescenarioid !== liveId) return;

      // Sound + toast
      playDing();
      setToastMsg('New observations received');
      setToastOpen(true);

      // Debounced reload (stale-while-revalidate)
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        reload();
      }, 150);
    };

    const onImagesUpdated = (payload) => {
      if (payload?.livescenarioid && payload.livescenarioid !== liveId) return;

      // Sound + toast
      playDing();
      setToastMsg('New imaging received');
      setToastOpen(true);

      // Debounced reload (stale-while-revalidate)
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        reload();
      }, 150);
    };

  socket.on('scenario:obs_updated', onObsUpdated);
  socket.on('scenario:bloods_updated', onBloodsUpdated);
  socket.on('scenario:images_updated', onImagesUpdated);

  return () => {
    socket.off('scenario:obs_updated', onObsUpdated);
    socket.off('scenario:bloods_updated', onBloodsUpdated);
    socket.off('scenario:images_updated', onImagesUpdated);
    clearTimeout(debounceRef.current);
  };
  }, [socket, liveId, reload, playDing]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setToastOpen(false);
  };

  return (
    <Snackbar
      open={toastOpen}
      onClose={handleClose}
      message={toastMsg}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      style={{ right: 150 }}
      autoHideDuration={30000}
      action={
        <IconButton size="small" color="inherit" aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  );
}
